import { Injectable, signal, WritableSignal, computed, Signal, effect, untracked, inject } from '@angular/core';

import { WebsiteData, ComponentItem, Page, Section, Style, WebElement, BusinessConfiguration, Theming, OpeningHours, Result, StyleOption, CacheState, ChangedOpeningHoursNotification, ImageGalleryImage } from '../model/interfaces/state.model';

import { ALLOWED_BUTTON_STYLES, ALLOWED_TEXT_STYLES, DEFAULT_BUSINESS_CONFIGURATION } from "../constants/state.constants";
import { CacheStatus, ComponentActions, ElementsType, FileType, ModeType, StyleType } from '../model/enums/state.enums';
import { DEFAULT_IMAGE_ALT_TEXT, DEFAULT_IMAGE_GALLERY_FOLDER, DEFAULT_ROOT_FOLDER, STORAGE_BUCKET, STORAGE_ROOT_FOLDER } from '../constants/global.constants';

import { SaveDataService } from '../../services/save-data.service';
import { UtilityService } from '../../services/utility.service';
import { logMethodCall } from '../../decorators/log-method.decorator';

import { v4 as uuidv4 } from 'uuid';
import { SpecifiedImageGalleryImage } from '../../components/composed/c-image-gallery/base-image-gallery/base-image-gallery.component';
import { FirestoreService } from '../../services/firestore.service';
import { LoggerService } from '../../services/logger.service';

@Injectable({
  providedIn: 'root'
})

export class WebsiteStateService {

  // #region: INITIALIZATION ###
  private _mode: WritableSignal<ModeType> = signal<ModeType>(ModeType.View);
  private _actions: WritableSignal<ComponentActions[]> = signal<ComponentActions[]>([]);
  private _cacheState: WritableSignal<CacheState> = signal<CacheState>({
    unsavedChanges: false,
    status: CacheStatus.Initialised,
    numberOfChanges: 0
  });
  private _activePageId: WritableSignal<string | null> = signal<string | null>(null);
  private _websiteData: WritableSignal<WebsiteData> = signal<WebsiteData>({
    name: "",
    configuration: DEFAULT_BUSINESS_CONFIGURATION,
    pages: {},
    sections: {},
    components: {},
    elements: {},
    styles: {}
  });
  private _imageGallery: WritableSignal<ImageGalleryImage[]> = signal<ImageGalleryImage[]>([]);

  private _websiteDataSnapshot: WebsiteData | null = null;
  private _cacheStatusSnapshot: CacheState = { unsavedChanges: false, status: CacheStatus.Initialised, numberOfChanges: 0 };

  private _isCookiePolicyAccepted: WritableSignal<boolean> = signal<boolean>(false);
  private _changedOpeningHourNotification: WritableSignal<ChangedOpeningHoursNotification> = signal<ChangedOpeningHoursNotification>({ showNotification: false, dates: [], notificationText: "" });

  // === ANALYTICS CONSENT STATE ===
  private _analyticsConsent: WritableSignal<boolean> = signal<boolean>(false);
  private _analyticsBufferedEvents: WritableSignal<any[]> = signal<any[]>([]);
  private _analyticsBufferedSessions: WritableSignal<any[]> = signal<any[]>([]);

  constructor(
    private saveDataService: SaveDataService,
    private firestoreService: FirestoreService,
    private logger: LoggerService,
    private util: UtilityService,
  ) {
    this.#createStateLoggerEffect$(this._mode, 'WS: Mode');
    this.#createStateLoggerEffect$(this._websiteData, 'WS: WebsiteData');
    this.#createStateLoggerEffect$(this._cacheState, 'WS: UnsavedChanges');
    this.#createStateLoggerEffect$(this._changedOpeningHourNotification, 'WS: ChangedOpeningHoursNotification');
    this.#createStateCacheEffect$();
  }


  initializeState(data: any): void {
    this.logger.log('Initialize State:');
    if (!data) {
      this.logger.error('Keine Daten zum Initialisieren des State.');
      return;
    }
    this._websiteData.set({
      name: data.name,
      configuration: this.util.deepMergeWithDefaults(data.configuration, DEFAULT_BUSINESS_CONFIGURATION),
      pages: data.pages,
      sections: data.sections,
      components: data.components,
      elements: data.elements,
      styles: data.styles
    });
  }

  initializeImageGallery(data: ImageGalleryImage[]): void {
    this.logger.log('Initialize Image Gallery:', data);
    if (!data) {
      this.logger.error('Keine Daten zum Initialisieren der Image Gallery.');
      return;
    }
    this._imageGallery.set(data);
  }

  public takeSnapshot(): void {
    this._websiteDataSnapshot = structuredClone(this._websiteData());
    this._cacheStatusSnapshot = structuredClone(this._cacheState());
    this.logger.log('Snapshot taken:', this._websiteDataSnapshot);
  }

  public discardChanges(): void {
    if (this._websiteDataSnapshot) {
      this._websiteData.set(structuredClone(this._websiteDataSnapshot));
      this._cacheState.set(structuredClone(this._cacheStatusSnapshot));
      this.logger.log('Changes discarded, state reset to snapshot:', this._websiteData());
    } else {
      this.logger.error('No snapshot available to discard changes.');
    }
  }
  // #endregion

  @logMethodCall()
  getDownloadURLs(elemId: string, format: string, fileType: FileType): { type: FileType, url: string } {
    let shopId = this.shopId();
    const url = `${STORAGE_ROOT_FOLDER}/${STORAGE_BUCKET}/${shopId}/${DEFAULT_ROOT_FOLDER}/${elemId}/image_${format}.${fileType}?timestamp=${new Date().getTime()}`;
    return { type: fileType, url: url }
  }
  // #endregion

  //   #####################################


  // ###########################################################################
  // #region: FEATURE: _websiteData

  //   #####################################
  //   #region: --> SELECTORS -- BASIC 

  get actions(): Signal<ComponentActions[]> {
    return this._actions.asReadonly();
  }

  set actions(actions: ComponentActions[]) {
    this._actions.set(actions);
  }

  removeAction(action: ComponentActions) {
    const currentActions = this._actions();
    const newActions = currentActions.filter(a => a !== action);
    this._actions.set(newActions);
  }


  //      #region:   >>> ImageGallery <<<
  get imageGallery(): Signal<ImageGalleryImage[]> {
    return this._imageGallery.asReadonly();
  }

  async setAndSaveImageGallery(images: SpecifiedImageGalleryImage[]): Promise<void> {
    const imageGallery: ImageGalleryImage[] = images
      .filter((img) => !img.isPlaceholder)
      .map((img) => {
        return {
          timestamp: img.timestamp,
          uuid: img.uuid,
          tags: img.tags
        };
      });
    this._imageGallery.set(imageGallery);
    await this.firestoreService.updateImageGallery(imageGallery);
  }

  getImageGalleryStoragePath(): string {
    const shopId = this.shopId();
    return `${STORAGE_ROOT_FOLDER}/${STORAGE_BUCKET}/${shopId}/${DEFAULT_ROOT_FOLDER}/${DEFAULT_IMAGE_GALLERY_FOLDER}/`;
  }

  getImageGalleryById(id: string): Signal<SpecifiedImageGalleryImage[]> {
    return computed(() => {
      const currentElement = this.getCurrentElement(id)();
      if (currentElement.success)
        return this.imageGallery().map((img) => ({
          ...img,
          showInGallery: currentElement.payload.config.images.includes(img.uuid),
          showAsThumbnail: currentElement.payload.config.thumbnails.includes(img.uuid),
          isPlaceholder: false,
        })
        );
      else
        return this.imageGallery().map((img) => ({
          ...img,
          showInGallery: false,
          showAsThumbnail: false,
          isPlaceholder: false,
        })
        );
    });
  }

  //      #region:   >>> websiteData <<<
  get websiteData() {
    return this._websiteData.asReadonly();
  }
  // #endregion

  //      #region:   >>> Configuration <<<
  get configuration(): Signal<BusinessConfiguration> {
    return computed(() => this._websiteData().configuration);
  }

  get shopId(): Signal<string | null> {
    return computed(() => this.configuration().shopId);
  }

  get storeName(): Signal<string | null> {
    return computed(() => this.configuration().contactData.name);
  }

  get ownerFirstName(): Signal<string | null> {
    return computed(() => this.configuration().contactData.owner.firstname);
  }

  get showOpeningBanner(): Signal<boolean> {
    return computed(() => this.configuration().showOpeningBanner);
  }

  get theming(): Signal<Theming> {
    return computed(() => this.configuration().theming);
  }

  get openingHours(): Signal<OpeningHours> {
    return computed(() => this.configuration().openingHours);
  }
  //#endregion

  //      #region:   >>> Pages <<<
  get pages(): Signal<Record<string, Page>> {
    return computed(() => this._websiteData().pages);
  }

  get allPages(): Signal<Page[]> {
    return computed(() => Object.values(this.pages()).map(page => page));
  }

  get pageKeys(): Signal<string[]> {
    return computed(() => Object.keys(this._websiteData().pages));
  }
  // #endregion

  //      #region:   >>> Sections <<<
  get sections(): Signal<Record<string, Section>> {
    return computed(() => this._websiteData().sections);
  }
  // #endregion

  //      #region:   >>> Components <<<
  get components(): Signal<Record<string, ComponentItem>> {
    return computed(() => this._websiteData().components);
  }
  // #endregion

  //      #region:   >>> Elements <<<
  get elements(): Signal<Record<string, WebElement>> {
    return computed(() => this._websiteData().elements);
  }
  // #endregion

  //      #region:   >>> Styles <<<
  get styles(): Signal<Record<string, Style[]>> {
    return computed(() => this._websiteData().styles);
  }
  // #endregion

  //      #region:   >>> ChangedOpeningHoursNotification <<<
  get changedOpeningHoursNotification(): Signal<ChangedOpeningHoursNotification> {
    return this._changedOpeningHourNotification.asReadonly();
  }
  // #endregion

  //      #region:   >>> Analytics Consent <<<
  get analyticsConsent(): Signal<boolean> {
    return this._analyticsConsent.asReadonly();
  }

  get analyticsBufferedEvents(): Signal<any[]> {
    return this._analyticsBufferedEvents.asReadonly();
  }

  get hasBufferedEvents(): Signal<boolean> {
    return computed(() => this._analyticsBufferedEvents().length > 0);
  }
  // #endregion

  // #endregion

  //   #####################################
  //   #region: --> SELECTORS -- COMPUTED
  get googleReviewLink(): Signal<string> {
    return computed(() => this.shopId() ? `https://search.google.com/local/writereview?placeid=${this.shopId()}` : '');
  }

  get sortedActiveSections(): Signal<Section[]> {
    return computed(() => {
      const page = this.activePage();
      if (!page) return [];
      return page.sections.map(sectionId => this._websiteData().sections[sectionId]).sort((a, b) => a.order - b.order);
    });
  }

  get sortedActiveComponents(): Signal<Record<string, ComponentItem[]>> {
    return computed(() => {
      const activeSections = this.sortedActiveSections();
      const components: Record<string, ComponentItem[]> = {};
      for (const section of activeSections) {
        // components[section.id] = section.components.map(componentId => this._websiteData().components[componentId]).sort((a, b) => a.order - b.order);

        components[section.id] = section.components.map(componentId => this._websiteData().components[componentId]);
      }
      return components;
    });
  }

  get sortedActiveComponentIds(): Signal<string[]> {
    return computed(() => {
      const activeComponents = this.sortedActiveComponents();
      return Object.values(activeComponents).reduce((acc, components) => [...acc, ...components.map(component => component.id)], [] as string[]);
    });
  }

  get navbarPages(): Signal<Array<{ group: string | null; pages: Page[] }>> {
    return computed(() => {
      const pagesArray = Object.values(this.pages());
      const navbarGroupArray = Object.values(this.configuration().navbarGroups);
      const navbarPages = pagesArray.filter(page => page.isInNavbar);

      // Mapping von ID zu Displayname erstellen
      const navbarGroupMap = navbarGroupArray.reduce((acc, group) => {
        acc[group.id] = group.displayName;
        return acc;
      }, {} as Record<string, string>);

      // Gruppieren der Seiten nach `navbarGroup`
      const groups = navbarPages.reduce((acc, page) => {
        const groupKey = page.navbarGroup ? navbarGroupMap[page.navbarGroup] : 'Ungrouped';
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(page);
        return acc;
      }, {} as Record<string, Page[]>);

      // Konvertieren in ein Array, das für die `@For`-Schleife geeignet ist
      return Object.entries(groups).map(([group, pages]) => ({ group, pages }));
    });
  }
  // #endregion

  //   #####################################
  //   #region: --> SELECTORS -- COMPUTED (including parameters)
  @logMethodCall()
  getCurrentElement(elemId: string): Signal<Result<WebElement, string>> {
    return computed(() => {
      const elements = this.elements();
      if (!elements) {
        return this.util.fail('No elements found', 500);
      }
      const element = elements[elemId];
      return element ? this.util.ok(element) : this.util.fail('Element not found', 404);
    });
  }

  @logMethodCall()
  getStylesForElement(elemId: string): Signal<Partial<Record<StyleType, string>>> {
    return computed(() => {
      const currentElement = this.getCurrentElement(elemId)();
      if (currentElement.success === false) return {};

      const styleId = currentElement.payload.styles;
      if (!styleId) return {};
      const style: Style[] = this.styles()[styleId];
      if (!style) return {};

      let res: Partial<Record<StyleType, string>> = {};
      style.forEach(s => {
        res[s.type] = s.type === 'font-size' ? `${s.value}px` : String(s.value);
      });
      return res;
    });
  }

  getStylesForID(styleId: string, eType: ElementsType): Signal<Style[]> {
    return computed(() => {
      let currentStyles = this._websiteData().styles[styleId] || [];
      currentStyles = currentStyles.map(style => {
        const { label, inputType } = this.#getLabelForStyleType(style.type, style.label);
        return { ...style, label, inputType };
      });

      if (eType === ElementsType.Text) {
        this.#textStyles.forEach(textStyle => {
          if (!currentStyles.find(style => style.type === textStyle.type)) {
            currentStyles.push(textStyle);
          }
        });
      } else if (eType === ElementsType.Button) {
        this.#buttonStyles.forEach(buttonStyle => {
          if (!currentStyles.find(style => style.type === buttonStyle.type)) {
            currentStyles.push(buttonStyle);
          }
        });
      }

      // Define a sorting function based on the properties of Style
      currentStyles.sort((a, b) => {
        if (a.type < b.type) return -1;
        if (a.type > b.type) return 1;
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });
      return currentStyles;
    });
  }

  @logMethodCall()
  getSingleElementIdByLabelAndComponent(componentId: string, label: string): Signal<Result<string, string>> {
    return computed(() => {
      const elements = this.components()[componentId].elements;
      if (!elements) {
        return this.util.fail('No elements found', 500);
      }
      const element = this.getSingleElementIdByLabel(elements, label)();
      return element ? this.util.ok(element) : this.util.fail('Element not found', 404);
    });
  }

  getSingleElementIdByLabel(elementIds: string[], label: string): Signal<string | undefined> {
    return computed(() => {
      const element = elementIds
        .map(id => this.elements()[id])
        .find(element => element && element.label === label);
      return element ? element.id : undefined;
    });
  }

  getSingleComposedElementIds(componentId: string, type: ElementsType): Signal<string[]> {
    return computed(() => {
      const elemIds: string[] = this.components()[componentId].elements;
      const currentElements$: WebElement[] = elemIds.map((elementId) => this.elements()[elementId])
      const composedElementIds = currentElements$.filter((element) => element.type === type).map((element) => element.id);
      return composedElementIds;
    });
  }

  getGroupComposedElementIds(componentId: string, type: ElementsType): Signal<string[][]> {
    return computed(() => {
      const elemIds: string[] = this.components()[componentId].elements;
      const currentElements$: WebElement[] = elemIds.map((elementId) => this.elements()[elementId])
      const composedElementIds = currentElements$.filter((element) => element.type === type);
      const groupedElementIds: Record<number, string[]> = composedElementIds.reduce((acc: Record<number, string[]>, element) => {
        const order = element.order ?? 0;
        if (!acc[order]) {
          acc[order] = [];
        }
        acc[order].push(element.id);
        return acc;
      }, {});
      return Object.keys(groupedElementIds).sort((a, b) => Number(a) - Number(b)).map(key => groupedElementIds[Number(key)]);

    });
  }

  deleteElementsFromComponent(componentId: string, elementIds: string[]): void {
    const currentElements = this.components()[componentId].elements;
    const newElements = currentElements.filter(id => !elementIds.includes(id));
    this.updateComponent(componentId, { elements: newElements });
  }



  getImageAltText(elemId: string | undefined): Signal<string> {
    return computed(() => {
      if (!elemId) return DEFAULT_IMAGE_ALT_TEXT;
      return this.elements()[elemId]?.options?.altText ?? DEFAULT_IMAGE_ALT_TEXT;
    });
  }
  // #endregion

  //   #####################################
  //   #region: --> CRUD OPERATIONS

  //      #region: >>> Configuration <<<
  updateConfiguration(updatedFields: Partial<BusinessConfiguration>) {
    this._websiteData.update(data => ({
      ...data,
      configuration: { ...data.configuration, ...updatedFields }
    }));
  }

  // TODO: To be refactored into the correct CRUD operations
  // #CRUD <<< OTHER >>>
  updateTheming(updatedFields: Partial<Theming>) {
    const currentTheming = this.configuration().theming;
    const mergedTheming = {
      ...currentTheming,
      ...updatedFields,
      colors: { ...currentTheming.colors, ...updatedFields.colors },
    };
    this.updateConfiguration({ theming: mergedTheming });
  }
  //#endregion

  //      #region: >>> Pages <<<
  addPage(pageId: string, page: Page) {
    if (this._websiteData().pages[pageId]) {
      this.logger.error(`Page mit ID ${pageId} existiert bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      pages: { ...data.pages, [pageId]: { ...page } }
    }));
  }

  updatePage(pageId: string, updatedFields: Partial<Page>) {
    if (this._websiteData().pages[pageId]) {
      this._websiteData.update(data => ({
        ...data,
        pages: { ...data.pages, [pageId]: { ...data.pages[pageId], ...updatedFields } }
      }));
    } else {
      this.logger.error(`Page mit ID ${pageId} existiert nicht.`);
    }
  }

  removePage(pageId: string) {
    if (!this._websiteData().pages[pageId]) {
      this.logger.error(`Page mit ID ${pageId} existiert nicht.`);
      return;
    }
    const { [pageId]: _, ...remainingPages } = this._websiteData().pages;
    this._websiteData.update(data => ({
      ...data,
      pages: remainingPages
    }));
  }
  //#endregion

  //      #region: >>> Sections <<<

  // CRUD methods for Sections have been implemented, but aren't used in the current version of the application.
  // Hence, when activated, the methods need to be tested and validated.
  addSection(sectionId: string, section: Section) {
    if (this._websiteData().sections[sectionId]) {
      this.logger.error(`Section mit ID ${sectionId} existiert bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      sections: {
        ...data.sections,
        [sectionId]: { ...section }
      }
    }));
  }

  updateSection(sectionId: string, updatedFields: Partial<Section>) {
    if (this._websiteData().sections[sectionId]) {
      this._websiteData.update(data => ({
        ...data,
        sections: {
          ...data.sections,
          [sectionId]: { ...data.sections[sectionId], ...updatedFields }
        }
      }));
    } else {
      this.logger.error(`Section mit ID ${sectionId} existiert nicht.`);
    }
  }

  /*
 removeSection(sectionId: string, pageId: string) {
   if (!this._websiteData().sections[sectionId]) {
     this.logger.error(`Section mit ID ${sectionId} existiert nicht.`);
     return;
   }
   const { [sectionId]: _, ...remainingSections } = this._websiteData().sections;
   this._websiteData.update(data => ({
     ...data,
     sections: remainingSections,
     pages: {
       ...data.pages,
       [pageId]: {
         ...data.pages[pageId],
         sections: data.pages[pageId].sections.filter(id => id !== sectionId)
       }
     }
   }));
 }
 */

  //#endregion

  //      #region: >>> Components <<<

  // CRUD methods for Components have been implemented, but aren't used in the current version of the application.
  // Hence, when activated, the methods need to be tested and validated.
  addComponent(componentId: string, component: ComponentItem) {
    if (this._websiteData().components[componentId]) {
      this.logger.error(`Component mit ID ${componentId} existiert bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      components: {
        ...data.components,
        [componentId]: { ...component }
      },
    }));
  }

  updateComponent(componentId: string, updatedFields: Partial<ComponentItem>) {
    if (this._websiteData().components[componentId]) {
      this._websiteData.update(data => ({
        ...data,
        components: {
          ...data.components,
          [componentId]: { ...data.components[componentId], ...updatedFields }
        }
      }));
    } else {
      this.logger.error(`Component mit ID ${componentId} existiert nicht.`);
    }
  }

  removeComponent(componentId: string, sectionId: string) {
    // Check if the component exists
    if (!this._websiteData().components[componentId]) {
      this.logger.error(`Component mit ID ${componentId} existiert nicht.`);
      return;
    }
    // Check if the component is used in other sections
    const isComponentUsedElsewhere = Object.values(this._websiteData().sections).some(section =>
      section.components.includes(componentId) && section.id !== sectionId
    );

    if (isComponentUsedElsewhere) {
      // Only remove the component from the requested section
      this._websiteData.update(data => ({
        ...data,
        sections: {
          ...data.sections,
          [sectionId]: {
            ...data.sections[sectionId],
            components: data.sections[sectionId].components.filter(id => id !== componentId)
          }
        }
      }));
    } else {
      // Remove the component completely
      const { [componentId]: _, ...remainingComponents } = this._websiteData().components;
      this._websiteData.update(data => ({
        ...data,
        components: remainingComponents,
        sections: {
          ...data.sections,
          [sectionId]: {
            ...data.sections[sectionId],
            components: data.sections[sectionId].components.filter(id => id !== componentId)
          }
        }
      }));
    }
  }

  //#endregion

  //      #region: >>> Elements <<<
  addElement(elementId: string, element: WebElement) {
    if (this._websiteData().elements[elementId]) {
      this.logger.error(`Element mit ID ${elementId} existiert bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      elements: {
        ...data.elements,
        [elementId]: { ...element }
      },
    }));
  }


  addElementToComponent(elementId: string, element: WebElement, componentId: string) {
    if (this._websiteData().elements[elementId]) {
      this.logger.error(`Element mit ID ${elementId} existiert bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      elements: {
        ...data.elements,
        [elementId]: { ...element }
      },
      components: {
        ...data.components,
        [componentId]: {
          ...data.components[componentId],
          elements: [...data.components[componentId].elements, elementId]
        }
      }
    }));
  }


  updateElement(elementId: string, updatedFields: Partial<WebElement>) {
    if (this._websiteData().elements[elementId]) {
      this._websiteData.update(data => ({
        ...data,
        elements: { ...data.elements, [elementId]: { ...data.elements[elementId], ...updatedFields } }
      }));
    } else {
      this.logger.error(`Element mit ID ${elementId} existiert nicht.`);
    }
  }

  // setImageAltText(elemId: string, altText: string) {
  //   this.updateElement(elemId, { options: { altText } });
  // }


  removeElement(elementId: string, componentId: string) {
    if (!this._websiteData().elements[elementId]) {
      this.logger.error(`Element mit ID ${elementId} existiert nicht.`);
      return;
    }
    const { [elementId]: _, ...remainingElements } = this._websiteData().elements;
    this._websiteData.update(data => ({
      ...data,
      elements: remainingElements,
      components: {
        ...data.components,
        [componentId]: {
          ...data.components[componentId],
          elements: data.components[componentId].elements.filter(id => id !== elementId)
        }
      }
    }));
  }

  //#endregion

  //      #region: >>> Styles <<<
  addStyle(elemId: string, styleId: string, styles: Style[]) {
    if (this._websiteData().styles[styleId]) {
      this.logger.error(`Styles mit ID ${styleId} existieren bereits.`);
      return;
    }
    this._websiteData.update(data => ({
      ...data,
      elements: { ...data.elements, [elemId]: { ...data.elements[elemId], styles: styleId } },
      styles: { ...data.styles, [styleId]: [...styles] }
    }));
    this.logger.log('Add Style', elemId, styleId, styles, this._websiteData().styles[styleId]);
  }

  updateStyle(elemId: string, styleId: string, updatedFields: Partial<Style>) {
    if (this._websiteData().styles[styleId]) {
      this.logger.log('Update Style', styleId, updatedFields, this._websiteData().styles[styleId]);
      this._websiteData.update(data => ({
        ...data,
        styles: {
          ...data.styles, [styleId]:
            data.styles[styleId].length === 0 || !data.styles[styleId].some(style => style.type === updatedFields.type) ?
              [...data.styles[styleId], updatedFields as Style] :
              data.styles[styleId].map(style => style.type === updatedFields.type ?
                { ...style, ...updatedFields } : style)
        }
      }));
    } else {
      this.logger.log(`Styles mit ID ${styleId} existieren nicht und muss für Element ${elemId} erstellt werden.`);
      this.addStyle(elemId, styleId, [updatedFields as Style]);
    }
  }

  removeStyle(styleId: string) {
    if (!this._websiteData().styles[styleId]) {
      this.logger.error(`Styles mit ID ${styleId} existieren nicht.`);
      return;
    }
    const { [styleId]: _, ...remainingStyles } = this._websiteData().styles;
    this._websiteData.update(data => ({
      ...data,
      styles: remainingStyles
    }));
  }
  // #endregion

  //      #region: >>> ChangedOpeningHoursNotification <<<
  updateChangedOpeningHoursNotification(updatedFields: Partial<ChangedOpeningHoursNotification>) {
    this._changedOpeningHourNotification.update(data => ({
      ...data,
      ...updatedFields
    }));
  }
  // #endregion

  //#endregion

  //  #####################################
  //  #region: --> EFFECTS

  #createStateLoggerEffect$<T extends { configuration?: any }>(signal: Signal<T>, label: string = 'State'): void {
    let previousValue: T | undefined;
    effect(() => {
      const newValue = signal();
      this.logger.group(`[StateLogger] ${label}`);
      this.logger.log('Previous Value:', previousValue);
      this.logger.log('New Value:', newValue);
      this.logger.groupEnd();
      previousValue = newValue;
    });
  }

  #createStateCacheEffect$(): void {
    effect(() => {
      this.saveDataService.saveToLocalStorage(this.websiteData());
      untracked(() => {
        if (this.cacheState().status === CacheStatus.Initialised) {
          this.setCacheStatus(CacheStatus.Ready);
        } else if (this.cacheState().status === CacheStatus.Ready) {
          this.setCacheStatus(CacheStatus.Listening);
        } else {
          this.setUnsavedChanges(true);
        }
      });
    });
  }
  //#endregion

  //   ####################################
  //   #region: --> Private Methoden
  #getLabelForStyleType(styleType: StyleType, defaultLabel: string = 'unknown'): { label: string, inputType: string, options: StyleOption[] | null } {
    const styleTypeMap: Partial<Record<StyleType, { label: string, inputType: string, options: StyleOption[] | null }>> = {
      [StyleType.Color]: { label: 'Farbe', inputType: 'color', options: null },
      [StyleType.BackgroundColor]: { label: 'Hintergrundfarbe', inputType: 'color', options: null },
      [StyleType.FontSize]: { label: 'Schriftgröße (in px)', inputType: 'number', options: null },
      [StyleType.FontWeight]: {
        label: 'Schriftstärke', inputType: 'select',
        options: [
          { label: 'Dünn', value: '300' },
          { label: 'Normal', value: '500' },
          { label: 'Fett', value: '700' }
        ]
      },
      [StyleType.TextAlign]: {
        label: 'Textausrichtung', inputType: 'select',
        options: [
          { label: 'Links', value: 'left' },
          { label: 'Zentriert', value: 'center' },
          { label: 'Rechts', value: 'right' }
        ]
      },
    };

    return styleTypeMap[styleType] || { label: defaultLabel, inputType: 'text', options: null };
  }

  #generateTextStyles(eType: ElementsType): Style[] {
    const allowedStyles = eType === ElementsType.Text ? ALLOWED_TEXT_STYLES : ALLOWED_BUTTON_STYLES;
    return allowedStyles.map((styleType) => {
      const { label, inputType, options } = this.#getLabelForStyleType(styleType);
      return { id: uuidv4(), label, type: styleType, inputType, value: null, options };
    });
  }

  #textStyles: Style[] = this.#generateTextStyles(ElementsType.Text);
  #buttonStyles: Style[] = this.#generateTextStyles(ElementsType.Button);
  //#endregion
  //#endregion

  // ###########################################################################
  // #region: FEATURE: _activePageId
  get activePage(): Signal<Page | null> {
    return computed(() => {
      const pageId = this._activePageId();
      return pageId ? this._websiteData().pages[pageId] : null;
    });
  }

  setActivePageId(pageId: string) {
    if (this._websiteData().pages[pageId]) {
      this._activePageId.set(pageId);
      this.logger.log(`Aktive Seite gesetzt auf ${pageId}`);
    } else {
      this.logger.error(`Page mit ID ${pageId} existiert nicht.`);
    }
  }

  setActivePageByUrl(url: string) {
    const pageId = this.pageKeys().find(pageId => this._websiteData().pages[pageId].url === url || '/' + this._websiteData().pages[pageId].url === url);
    if (pageId) {
      this.logger.log(`Aktive Seite basierend auf URL ${url} gefunden: ${pageId}`);
      this.setActivePageId(pageId);
    } else {
      this.logger.error(`Keine Seite mit der URL ${url} gefunden.`);
    }
  }
  //#endregion

  // ###########################################################################
  // #region: FEATURE: _mode
  get mode(): Signal<ModeType> {
    return this._mode.asReadonly();
  }

  get isEditMode(): Signal<boolean> {
    return computed(() => this._mode() === ModeType.Edit);
  }

  setMode(mode: ModeType) {
    this._mode.set(mode);
  }

  toggleMode() {
    this._mode.update(mode => mode === ModeType.Edit ? ModeType.View : ModeType.Edit);
  }
  // #endregion

  // #region: FEATURE: _cacheState
  get cacheState(): Signal<CacheState> {
    return this._cacheState.asReadonly();
  }

  get hasUnsavedChanges(): Signal<boolean> {
    return computed(() => this.cacheState().unsavedChanges);
  }

  get numberOfUnsavedChanges(): Signal<number> {
    return computed(() => this.cacheState().numberOfChanges);
  }

  setUnsavedChanges(unsavedChanges: boolean) {
    this._cacheState.update(cacheState => ({
      ...cacheState,
      unsavedChanges,
      numberOfChanges: unsavedChanges ? cacheState.numberOfChanges + 1 : 0
    }));
  }

  setCacheStatus(status: CacheStatus) {
    this._cacheState.update(cacheState => ({
      ...cacheState,
      status,
    }));
  }

  toggleUnsavedChanges() {
    this.setUnsavedChanges(!this.cacheState().unsavedChanges);
  }
  // #endregion


  // ###########################################################################
  // #region: FEATURE: _ccAccepted
  get ccAccepted(): Signal<boolean> {
    return this._isCookiePolicyAccepted.asReadonly();
  }

  setCCAccepted(value: boolean) {
    this._isCookiePolicyAccepted.set(value);
  }
  // #endregion

  // ###########################################################################
  // #region: FEATURE: _analyticsConsent
  setAnalyticsConsent(consent: boolean): void {
    this._analyticsConsent.set(consent);
    this.logger.log('Analytics consent updated:', consent);
  }

  addBufferedEvent(event: any): void {
    this._analyticsBufferedEvents.update(events => [...events, event]);
    this.logger.log('Event buffered:', event);
  }

  getBufferedEvents(): any[] {
    return this._analyticsBufferedEvents();
  }

  markEventsAsProcessed(processedEventIds: string[]): void {
    this._analyticsBufferedEvents.update(events => 
      events.map(event => 
        processedEventIds.includes(event.id) 
          ? { ...event, processed: true, processedAt: new Date().toISOString() }
          : event
      )
    );
    this.logger.log('Events marked as processed:', processedEventIds.length);
  }

  getUnprocessedEvents(): any[] {
    return this._analyticsBufferedEvents().filter(event => !event.processed);
  }

  // Session buffering methods
  addBufferedSession(session: any): void {
    this._analyticsBufferedSessions.update(sessions => [...sessions, session]);
    this.logger.log('Session buffered:', session);
  }

  getBufferedSessions(): any[] {
    return this._analyticsBufferedSessions();
  }

  markSessionsAsProcessed(processedSessionIds: string[]): void {
    this._analyticsBufferedSessions.update(sessions => 
      sessions.map(session => 
        processedSessionIds.includes(session.id) 
          ? { ...session, processed: true, processedAt: new Date().toISOString() }
          : session
      )
    );
    this.logger.log('Sessions marked as processed:', processedSessionIds.length);
  }

  getUnprocessedSessions(): any[] {
    return this._analyticsBufferedSessions().filter(session => !session.processed);
  }
  // #endregion

}